/**
 * First-party attribution capture for ad-click IDs.
 *
 * Persists `gclid` and `fbclid` from URL query params into two separate
 * first-party cookies (`_fra` for gclid, `_frb` for fbclid). Each runs on
 * every page load so attribution survives non-booking navigation and
 * outlives the consent-gated third-party cookies set by Google Ads / Meta.
 *
 * Cookie values are obfuscated (`f1.<xor+base64url>`) so they don't read
 * as recognisable click-IDs in casual inspection or cookie audits. The
 * encoding is symmetric and deterministic — see `AttributionCipher.php`
 * for the matching server-side implementation and the GTM custom-JS
 * snippet at the bottom of this file for read-side parity.
 *
 * Last-touch semantics: a fresh URL value overwrites any prior cookie
 * value of the same kind. The two cookies are updated independently —
 * `?gclid` alone won't touch `_frb`, and vice versa.
 *
 * Legacy cookies (`froots_gclid` / `froots_fbclid`, plus the brief
 * plain-text iteration of `_fra` / `_frb`) are migrated into the new
 * encoded form on the next page load and then expired.
 *
 * Legal basis: berechtigtes Interesse nach DSGVO Erwägungsgrund 47
 * (Direktmarketing / Kampagnen-Attribution für eigene Werbeausgaben),
 * konsistent mit dem bestehenden Pattern für `s_id` und `fsid`. Keine
 * Cookiebot-/Klaro-Consent-Prüfung — die Cookies sind technisch
 * funktional notwendig für ROI-Messung der eigenen Ad-Spend.
 *
 * --- GTM Custom JavaScript Variable (read side) ---------------------------
 * Paste into GTM as `Custom JavaScript`, swap the COOKIE constant for `_frb`
 * to read fbclid:
 *
 *     function () {
 *         var COOKIE = '_fra'; // '_fra' = gclid, '_frb' = fbclid
 *         var KEY = 'fr.x9';
 *         var PREFIX = 'f1.';
 *         var m = document.cookie.match(
 *             new RegExp('(?:^|; )' + COOKIE + '=([^;]*)'),
 *         );
 *         if (!m) return undefined;
 *         var raw = decodeURIComponent(m[1]);
 *         if (raw.indexOf(PREFIX) !== 0) return raw;
 *         try {
 *             var enc = raw.slice(PREFIX.length)
 *                 .replace(/-/g, '+').replace(/_/g, '/');
 *             enc += '='.repeat((4 - enc.length % 4) % 4);
 *             var bin = atob(enc);
 *             var out = '';
 *             for (var i = 0; i < bin.length; i++) {
 *                 out += String.fromCharCode(
 *                     bin.charCodeAt(i) ^ KEY.charCodeAt(i % KEY.length),
 *                 );
 *             }
 *             return out;
 *         } catch (e) {
 *             return undefined;
 *         }
 *     }
 */

const COOKIE_GCLID = '_fra';
const COOKIE_FBCLID = '_frb';
const LEGACY_GCLID = 'froots_gclid';
const LEGACY_FBCLID = 'froots_fbclid';
// Third-party cookies set by GTM Conversion Linker (`_gcl_aw`) and Meta Pixel
// (`_fbc`). When our own `_fra` / `_frb` are missing, the trailing click-ID
// in these cookies is the fallback source — covers users who clicked the ad
// before this capture script existed and consent-banner race conditions.
const GCL_AW_COOKIE = '_gcl_aw';
const FBC_COOKIE = '_fbc';
const GCL_AW_PATTERN = /^GCL\.\d+\.(.+)$/;
const FBC_PATTERN = /^fb\.\d+\.\d+\.(.+)$/;
const TTL_DAYS = 90;
const KEY = 'fr.x9';
const PREFIX = 'f1.';

const textEncoder = new TextEncoder();
const textDecoder = new TextDecoder();

const xor = (input: Uint8Array): Uint8Array => {
    const keyBytes = textEncoder.encode(KEY);
    const out = new Uint8Array(input.length);
    for (let i = 0; i < input.length; i++) {
        out[i] = input[i] ^ keyBytes[i % keyBytes.length];
    }
    return out;
};

const bytesToBinary = (bytes: Uint8Array): string => {
    let bin = '';
    for (let i = 0; i < bytes.length; i++) bin += String.fromCharCode(bytes[i]);
    return bin;
};

const binaryToBytes = (bin: string): Uint8Array => {
    const bytes = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
    return bytes;
};

const encodeValue = (plain: string): string => {
    const xored = xor(textEncoder.encode(plain));
    return PREFIX + btoa(bytesToBinary(xored))
        .replace(/\+/g, '-')
        .replace(/\//g, '_')
        .replace(/=+$/, '');
};

const decodeValue = (raw: string): string | null => {
    if (!raw.startsWith(PREFIX)) {
        // Legacy plain-text cookie — return as-is so the read path still
        // resolves; the next captureFromUrl pass will rewrite it encoded.
        return raw;
    }
    try {
        const payload = raw.slice(PREFIX.length);
        const padded = payload.replace(/-/g, '+').replace(/_/g, '/');
        const padding = (4 - (padded.length % 4)) % 4;
        const decoded = xor(binaryToBytes(atob(padded + '='.repeat(padding))));
        return textDecoder.decode(decoded);
    } catch {
        return null;
    }
};

const readCookie = (name: string): string | undefined => {
    const escaped = name.replace(/[-/\\^$*+?.()|[\]{}]/g, '\\$&');
    const match = document.cookie.match(new RegExp('(?:^|; )' + escaped + '=([^;]*)'));
    return match ? decodeURIComponent(match[1]) : undefined;
};

const writeCookie = (name: string, value: string, maxAgeSeconds: number): void => {
    const secure = window.location.protocol === 'https:' ? '; Secure' : '';
    document.cookie = `${name}=${encodeURIComponent(value)}; max-age=${maxAgeSeconds}; path=/; SameSite=Lax${secure}`;
};

const expireCookie = (name: string): void => {
    document.cookie = `${name}=; max-age=0; path=/`;
};

const sanitize = (raw: string | null | undefined): string | undefined => {
    if (!raw) return undefined;
    const trimmed = raw.trim();
    if (trimmed.length === 0 || trimmed.length > 512) return undefined;
    return trimmed;
};

const readDecodedCookie = (name: string): string | undefined => {
    const raw = readCookie(name);
    if (!raw) return undefined;
    const decoded = decodeValue(raw);
    return sanitize(decoded);
};

const extractFromGclAw = (raw: string | undefined): string | undefined => {
    if (!raw) return undefined;
    const match = raw.match(GCL_AW_PATTERN);
    return match ? sanitize(match[1]) : undefined;
};

const extractFromFbc = (raw: string | undefined): string | undefined => {
    if (!raw) return undefined;
    const match = raw.match(FBC_PATTERN);
    return match ? sanitize(match[1]) : undefined;
};

/**
 * Returns the current attribution payload. Falls back to legacy cookies so
 * users captured under the previous scheme don't lose attribution during
 * the migration window.
 */
export const readPayload = (): { gclid?: string; fbclid?: string } => {
    const out: { gclid?: string; fbclid?: string } = {};
    const gclid =
        readDecodedCookie(COOKIE_GCLID) ??
        extractFromGclAw(readCookie(GCL_AW_COOKIE)) ??
        sanitize(readCookie(LEGACY_GCLID));
    const fbclid =
        readDecodedCookie(COOKIE_FBCLID) ??
        extractFromFbc(readCookie(FBC_COOKIE)) ??
        sanitize(readCookie(LEGACY_FBCLID));
    if (gclid) out.gclid = gclid;
    if (fbclid) out.fbclid = fbclid;
    return out;
};

/**
 * Reads `?gclid` / `?fbclid` from the URL and refreshes the corresponding
 * cookies if anything has changed (URL hit, format upgrade from legacy, or
 * raw legacy cookie present). Idempotent and safe to call on every page.
 */
export const captureFromUrl = (): void => {
    if (typeof window === 'undefined' || typeof document === 'undefined') return;

    const params = new URLSearchParams(window.location.search);
    const urlGclid = sanitize(params.get('gclid'));
    const urlFbclid = sanitize(params.get('fbclid'));

    const rawGclid = readCookie(COOKIE_GCLID);
    const rawFbclid = readCookie(COOKIE_FBCLID);

    const existingGclid = rawGclid !== undefined ? sanitize(decodeValue(rawGclid)) : undefined;
    const existingFbclid = rawFbclid !== undefined ? sanitize(decodeValue(rawFbclid)) : undefined;

    const legacyGclid = sanitize(readCookie(LEGACY_GCLID));
    const legacyFbclid = sanitize(readCookie(LEGACY_FBCLID));

    const gclAwGclid = extractFromGclAw(readCookie(GCL_AW_COOKIE));
    const fbcFbclid = extractFromFbc(readCookie(FBC_COOKIE));

    const nextGclid = urlGclid ?? existingGclid ?? gclAwGclid ?? legacyGclid;
    const nextFbclid = urlFbclid ?? existingFbclid ?? fbcFbclid ?? legacyFbclid;

    const needsWrite = (raw: string | undefined, next: string | undefined): boolean => {
        if (!next) return false;
        if (raw === undefined) return true;
        if (!raw.startsWith(PREFIX)) return true; // upgrade legacy plain-text format
        const decoded = sanitize(decodeValue(raw));
        return decoded !== next;
    };

    if (nextGclid && needsWrite(rawGclid, nextGclid)) {
        writeCookie(COOKIE_GCLID, encodeValue(nextGclid), TTL_DAYS * 86400);
    }
    if (nextFbclid && needsWrite(rawFbclid, nextFbclid)) {
        writeCookie(COOKIE_FBCLID, encodeValue(nextFbclid), TTL_DAYS * 86400);
    }
    if (legacyGclid !== undefined) expireCookie(LEGACY_GCLID);
    if (legacyFbclid !== undefined) expireCookie(LEGACY_FBCLID);
};
